import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { bodyToHtml, buildCommEmailDocument, parseCommEmailBrand, type CommEmailBrand } from '@shared/email/commEmailView';
import { formatUnknownError } from './commUi';

type BrandRow = {
    logo_url: string | null;
    header_color: string | null;
    signature: string | null;
    footer_text: string | null;
};

function brandFromRow(row: BrandRow | undefined): CommEmailBrand {
    return parseCommEmailBrand(
        row
            ? {
                  logo_url: row.logo_url || '',
                  header_color: row.header_color || '',
                  signature: row.signature || '',
                  footer_text: row.footer_text || '',
              }
            : null
    );
}

export function EmailBrandPanel() {
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<CommEmailBrand | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const query = useQuery({
        queryKey: ['admin', 'comm-email-brand'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<BrandRow[]>('admin_get_comm_email_brand');
            if (error) throw error;
            return brandFromRow(normalizeRpcResponse(data)[0]);
        },
    });

    const current = draft ?? query.data ?? null;
    const preview = useMemo(() => {
        if (!current) return '';
        return buildCommEmailDocument({
            subject: 'Así se ve el correo',
            headline: 'Así se ve el correo',
            bodyHtml: bodyToHtml('Hola Ana,\n\nEste es el texto del mensaje.\n\n[Abrir AiPeces]', 'https://www.aipeces.com/get-app'),
            preheader: 'Una línea junto al asunto',
            brand: current,
            unsubscribeUrl: '#',
        });
    }, [current]);

    const save = useMutation({
        mutationFn: async (brand: CommEmailBrand) => {
            const { error } = await dbClient.rpc('admin_save_comm_email_brand', {
                p_logo_url: brand.logoUrl.trim(),
                p_header_color: brand.headerColor,
                p_signature: brand.signature.trim(),
                p_footer_text: brand.footerText.trim(),
            });
            if (error) throw error;
        },
        onSuccess: () => {
            setDraft(null);
            setNotice('Guardado. Los próximos correos lo usan en menos de un minuto.');
            queryClient.invalidateQueries({ queryKey: ['admin', 'comm-email-brand'] });
        },
    });

    if (query.isLoading || !current) {
        return (
            <div className="flex justify-center p-10">
                <Loader2 className="animate-spin text-muted-foreground" />
            </div>
        );
    }
    if (query.isError) {
        return <p className="text-sm text-destructive">{formatUnknownError(query.error, 'No se pudo cargar el aspecto')}</p>;
    }

    const set = (patch: Partial<CommEmailBrand>) => setDraft({ ...current, ...patch });

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                    Este marco lo usan las reglas, las campañas, la serie de registro, la tienda y las licencias.
                    La tienda mantiene su propio pie de pedido. El texto de cada mensaje se edita en su sitio.
                    El color tiene que ser oscuro: la cabecera va en blanco.
                </p>
                {notice && <p className="text-sm text-primary">{notice}</p>}
                {save.isError && (
                    <p className="text-sm text-destructive">{formatUnknownError(save.error, 'No se pudo guardar')}</p>
                )}
                <label className="block text-sm">
                    Color de cabecera
                    <input
                        type="color"
                        className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-border bg-background"
                        value={current.headerColor}
                        onChange={(e) => set({ headerColor: e.target.value })}
                    />
                </label>
                <label className="block text-sm">
                    Firma
                    <input
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                        value={current.signature}
                        maxLength={120}
                        onChange={(e) => set({ signature: e.target.value })}
                    />
                </label>
                <label className="block text-sm">
                    Pie legal
                    <textarea
                        className="mt-1 min-h-[120px] w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                        value={current.footerText}
                        maxLength={800}
                        onChange={(e) => set({ footerText: e.target.value })}
                    />
                </label>
                <label className="block text-sm">
                    Logo (https). Vacío usa el logo de AiPeces.
                    <input
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs"
                        value={current.logoUrl}
                        placeholder="https://www.aipeces.com/assets/fishwe-logo.png"
                        onChange={(e) => set({ logoUrl: e.target.value })}
                    />
                </label>
                <button
                    type="button"
                    className="rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-60"
                    disabled={save.isPending}
                    onClick={() => save.mutate(current)}
                >
                    {save.isPending ? 'Guardando…' : 'Guardar aspecto'}
                </button>
            </div>
            <div className="min-h-[520px] overflow-hidden rounded-lg border border-slate-200" style={{ colorScheme: 'light' }}>
                <iframe
                    title="Vista previa del marco"
                    srcDoc={preview}
                    sandbox=""
                    referrerPolicy="no-referrer"
                    className="h-full min-h-[520px] w-full border-0 bg-[#f8fafc]"
                    style={{ colorScheme: 'light' }}
                />
            </div>
        </div>
    );
}
